import { useEffect } from 'react';
import { X, Command } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

const SHORTCUT_SECTIONS = [
  {
    title: 'Navigation',
    items: [
      { keys: [MOD, 'Shift', 'T'], label: 'Go to Today' },
      { keys: [MOD, 'Shift', 'I'], label: 'Go to Inbox' },
      { keys: [MOD, 'Shift', 'K'], label: 'Go to Calls' },
      { keys: [MOD, 'Shift', 'R'], label: 'Go to Reading' },
      { keys: [MOD, 'Shift', 'M'], label: 'Go to Payments (Money)' },
      { keys: [MOD, 'Shift', 'C'], label: 'Go to Calendar' },
      { keys: [MOD, 'Shift', 'A'], label: 'Go to Analytics' },
      { keys: [MOD, 'Shift', 'O'], label: 'Go to Commitments (Obligations)' },
      { keys: [MOD, 'Shift', 'L'], label: 'Go to Templates (Library)' },
    ],
  },
  {
    title: 'Actions',
    items: [
      { keys: [MOD, 'K'], label: 'Open PolkTaskHub (command bar)' },
      { keys: [MOD, '/'], label: 'Global search' },
      { keys: [MOD, 'Shift', 'N'], label: 'Toggle Notes panel' },
      { keys: ['Hold Space'], label: 'Voice capture → Inbox' },
      { keys: ['Hold Shift + Space'], label: 'Voice → Commitment' },
      { keys: ['?'], label: 'Open this cheat sheet' },
      { keys: ['Esc'], label: 'Close panels / modals' },
    ],
  },
];

function Kbd({ children }) {
  return (
    <kbd
      className="inline-flex items-center justify-center min-w-[24px] h-[24px] px-1.5 rounded-md text-[10px] font-semibold font-mono"
      style={{
        background: 'var(--pd-panel-solid)',
        border: '1px solid var(--pd-border)',
        color: 'var(--pd-text)',
        boxShadow: 'inset 0 -1px 0 var(--pd-border)',
      }}
    >
      {children}
    </kbd>
  );
}

export default function ShortcutsModal({ isOpen, onClose }) {
  useBodyScrollLock(isOpen);
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      data-testid="shortcuts-modal"
      className="fixed inset-0 z-[60] overflow-y-auto"
      style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(6px)' }}
    >
      <div className="flex min-h-full items-center justify-center px-4 py-8" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[620px] rounded-[24px] overflow-hidden"
        style={{
          background: 'var(--pd-popover)',
          border: '1px solid var(--pd-popover-border)',
          boxShadow: 'var(--pd-shadow-3)',
          maxHeight: 'calc(100vh - 4rem)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: '1px solid var(--pd-border)' }}
        >
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--pd-panel-2)' }}
            >
              <Command className="w-4 h-4" style={{ color: 'var(--pd-focus)' }} />
            </div>
            <div>
              <div className="text-[14px] font-semibold" style={{ color: 'var(--pd-text)' }}>
                Keyboard shortcuts
              </div>
              <div className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
                Move through PolkDesQ at the speed of thought
              </div>
            </div>
          </div>
          <button
            data-testid="shortcuts-modal-close"
            onClick={onClose}
            className="p-1.5 rounded-lg"
            style={{ color: 'var(--pd-muted)' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto custom-scrollbar flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SHORTCUT_SECTIONS.map((section) => (
              <div key={section.title}>
                <div
                  className="text-[9px] uppercase tracking-[0.14em] font-bold mb-2.5"
                  style={{ color: 'var(--pd-subtle)' }}
                >
                  {section.title}
                </div>
                <div className="space-y-1.5">
                  {section.items.map((item, idx) => (
                    <div
                      key={`${section.title}-${item.label}-${idx}`}
                      className="flex items-center justify-between gap-3 px-2.5 py-1.5 rounded-xl"
                      style={{ background: 'var(--pd-panel-2)' }}
                    >
                      <span className="text-[12px]" style={{ color: 'var(--pd-text)' }}>
                        {item.label}
                      </span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {item.keys.map((k, i) => (
                          <span key={`${item.label}-key-${i}-${k}`} className="flex items-center gap-1">
                            {i > 0 && (
                              <span className="text-[9px]" style={{ color: 'var(--pd-subtle)' }}>
                                +
                              </span>
                            )}
                            <Kbd>{k}</Kbd>
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="px-5 py-3 flex items-center justify-between text-[10px] flex-shrink-0"
          style={{ borderTop: '1px solid var(--pd-border)', color: 'var(--pd-subtle)' }}
        >
          <span>
            Press <Kbd>?</Kbd> anytime to reopen.
          </span>
          <span>
            Close with <Kbd>Esc</Kbd>
          </span>
        </div>
      </div>
      </div>
    </div>
  );
}
